import { useEffect, useMemo } from 'react';
import { useAccountSchoolsLazyQuery, useCustomersQuery } from '@api/graphql-codegen';
import Button from '@src/components/button/Button';
import Select from '@src/components/select/Select';
import { SelectOption } from '@types';
import { RankingApproach, SimulateMode, SimulateSchoolState } from './useSimulateSchool';
import styles from './SimulateSchool.module.scss';

const MODES: { mode: SimulateMode; label: string }[] = [
  { mode: 'me', label: 'As me' },
  { mode: 'mine', label: 'My school' },
  { mode: 'other', label: 'Another school' },
];

const APPROACHES: { approach: RankingApproach; label: string }[] = [
  { approach: 'new', label: 'New approach' },
  { approach: 'old', label: 'Old approach' },
];

type Props = {
  simulate: SimulateSchoolState;
};

/**
 * OP-3227: choose whose access the AI results are computed for. Super Admin pages only; the server
 * refuses a simulated school from anyone else, so this control grants nothing by itself.
 */
function SimulateSchool({ simulate }: Props) {
  const { state, setState, target, ownSchoolName, approach } = simulate;

  const { data: customersData, loading: customersLoading } = useCustomersQuery({ skip: state.mode !== 'other' });
  const [loadSchools, { data: schoolsData, loading: schoolsLoading }] = useAccountSchoolsLazyQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (state.mode === 'other' && state.accountId) {
      loadSchools({ variables: { input: { id: state.accountId } } });
    }
  }, [state.mode, state.accountId, loadSchools]);

  const accountOptions = useMemo<SelectOption[]>(
    () => (customersData?.allCustomers ?? [])
      .filter((c): c is NonNullable<typeof c> => Boolean(c?.id))
      .map((c) => ({ label: c.name || c.id, value: c.id })),
    [customersData]
  );

  const schools = useMemo(
    () => (schoolsData?.accountSchools?.schools ?? []).filter((s): s is NonNullable<typeof s> => Boolean(s?.id)),
    [schoolsData]
  );
  const schoolOptions = useMemo<SelectOption[]>(
    () => schools.map((s) => ({ label: s.name || s.id, value: s.id })),
    [schools]
  );

  const choose = (mode: SimulateMode) => setState((current) => ({ ...current, mode }));

  const chooseApproach = (next: RankingApproach) => setState((current) => ({ ...current, approach: next }));

  const chooseAccount = (option: SelectOption) =>
    setState((current) => ({ ...current, mode: 'other', accountId: option.value || undefined, school: undefined }));

  const chooseSchool = (option: SelectOption) => {
    const school = schools.find((s) => s.id === option.value);
    setState((current) => ({
      ...current,
      school: school ? { id: school.id, name: school.name ?? school.id, grades: school.grades } : undefined,
    }));
  };

  return (
    <div className={styles.banner} role="group" aria-label="Simulate a school">
      <div className={styles.row}>
        <span className={styles.title}>Simulate</span>
        <div className={styles.modes}>
          {MODES.map(({ mode, label }) => (
            <Button
              key={mode}
              label={mode === 'mine' && ownSchoolName ? `${label} (${ownSchoolName})` : label}
              size="tiny"
              color={state.mode === mode ? 'greenbackground' : 'green'}
              outline={state.mode !== mode}
              onClick={() => choose(mode)}
            />
          ))}
        </div>
      </div>
      {state.mode === 'other' && (
        <div className={styles.pickers}>
            <Select
              inline={false}
              placeholder="Choose an account"
              options={accountOptions}
              value={state.accountId ?? null}
              onChange={chooseAccount}
              loading={customersLoading}
              className={styles.picker}
            />
            <Select
              inline={false}
              placeholder={state.accountId ? 'Choose a school' : 'Choose an account first'}
              options={schoolOptions}
              value={state.school?.id ?? null}
              onChange={chooseSchool}
              loading={schoolsLoading}
              disabled={!state.accountId}
              className={styles.picker}
            />
        </div>
      )}

      <div className={styles.row}>
        <span className={styles.title}>Ranking</span>
        <div className={styles.modes}>
          {APPROACHES.map(({ approach: option, label }) => (
            <Button
              key={option}
              label={label}
              size="tiny"
              color={approach === option ? 'greenbackground' : 'green'}
              outline={approach !== option}
              onClick={() => chooseApproach(option)}
            />
          ))}
        </div>
      </div>

      <p className={styles.status}>
        {target
          ? <>Results are what a teacher at <strong>{target.name}</strong> would see, with that school&apos;s exclusions applied. </>
          : state.mode === 'other'
            ? 'Choose an account and a school. Until then, results use your own access. '
            : 'Results use your own Super Admin access, which skips school exclusions. '}
        {approach === 'new'
          ? 'New approach: op-ai ranks only the lessons that school may open.'
          : 'Old approach: op-ai ranks the whole catalogue, then locked lessons are removed, so fewer results remain.'}
      </p>
    </div>
  );
}

export default SimulateSchool;
